import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { ChatWidget, Message, ChatMode } from '@/components/ChatWidget';
import ContactOptionsExpanded from '@/components/ContactOptionsExpanded';
import { ProfileCompletionModal } from '@/components/LazyComponents';
import { StyleInjector } from '@/components/chat/StyleInjector';
import { useChatStore, UserData } from '@/store/chatStore';
import { useSocket } from '@/hooks/useSocket';
import { v4 as uuidv4 } from 'uuid';
import { getBotStatus } from '@/utils/getBotStatus';
import { detectOSChatbot, isMobileDevice } from '@/utils/detectOsAndBrowser';
import { getTokenUserFromCookieChatbot } from '@/utils/chatbotGetToken';
import { openCallChatbot, openCallRecommender } from '@/utils/openCall';
import * as UrlUtils from '@/utils/urlUtils';
import {
  CHATBOT_MODE_CHATBOT,
  CHATBOT_MODE_RECOMMENDER,
  CHATBOT_WHATSAPP_MESSAGE
} from '@/utils/constants';

const verboseChatbot = import.meta.env.VITE_APP_VERBOSE === 'true';

interface AppProps {
  initialUuid: string;
}

export function App({ initialUuid }: AppProps) {
  const [uuid, setUuid] = useState(initialUuid);

  // Store state
  const {
    isOpen,
    isFirstRender,
    isConnected,
    isResetting,
    isDropdownExpanded,
    messages: storeMessages,
    isLoading,
    isRecording,
    hasConversationHistory,
    configFeatureFlag,
    userData,
    currentMode,
    navigationState,
    welcomeNavigationState,
    isCheckingBotStatus,
    chatbotStatusIsOnline,
    recommenderStatusIsOnline,
    skipMaintenanceMode,
    openCount,
    wasMinimized,
    isAuthenticated,
    hasPhoneNumber,
    token,
    unreadMessageCount,
    showProfileCompletionModal,
    // Actions
    setIsOpen,
    setIsFirstRender,
    setNavigationState,
    setWelcomeNavigationState,
    setIsDropdownExpanded,
    setDetectedOs,
    incrementOpenCount,
    setWasMinimized,
    addMessage,
    setInputValue,
    setIsRecording,
    setIsCheckingBotStatus,
    setChatbotStatusIsOnline,
    setRecommenderStatusIsOnline,
    setSkipMaintenanceMode,
    setIsAuthenticated,
    setUnreadMessageCount,
    setShowProfileCompletionModal,
    setHasPhoneNumber,
    setUserData,
  } = useChatStore();

  // Socket connection
  const { socket, sendMessage: socketSendMessage, sendHitlDecision, resetChat } = useSocket({
    uuid,
    onUuidChange: setUuid,
  });

  // Check bot status on mount
  useEffect(() => {
    if (verboseChatbot) {
      console.log('🔍 Checking bot status...');
    }

    // ========== HARDCODE PARA TESTING LOCAL ==========

    /*
    // Opción 3: Ambos offline
    setChatbotStatusIsOnline(false);
    setRecommenderStatusIsOnline(false);
    setIsCheckingBotStatus(false);
    return;
    */

    // ========== FIN HARDCODE ==========

    getBotStatus(
      { value: chatbotStatusIsOnline, set: setChatbotStatusIsOnline },
      { value: recommenderStatusIsOnline, set: setRecommenderStatusIsOnline }
    )
      .then((result) => {
        setChatbotStatusIsOnline(result.chatbot);
        setRecommenderStatusIsOnline(result.recommender);
        setSkipMaintenanceMode(result.skipMaintenanceMode || false);
        setIsCheckingBotStatus(false);

        if (verboseChatbot) {
          console.log('✅ Bot status checked:', {
            chatbot: result.chatbot,
            recommender: result.recommender,
            skipMaintenance: result.skipMaintenanceMode,
          });
        }
      })
      .catch((error) => {
        console.error('❌ Error checking bot status:', error);
        setIsCheckingBotStatus(false);
        setChatbotStatusIsOnline(false);
        setRecommenderStatusIsOnline(false);
        setSkipMaintenanceMode(false);
      });
  }, []);

  // Show maintenance message when chat opens and bot is offline
  useEffect(() => {
    if (!isOpen || isCheckingBotStatus) return;

    const mode = currentMode();
    const isCurrentModeOffline =
      (mode === CHATBOT_MODE_CHATBOT && !chatbotStatusIsOnline) ||
      (mode === CHATBOT_MODE_RECOMMENDER && !recommenderStatusIsOnline);

    // Only show maintenance message if bot is offline and no messages exist yet
    if (isCurrentModeOffline && !skipMaintenanceMode) {
      const currentMessages = storeMessages[mode] || [];
      // Check if maintenance message already exists
      const hasMaintenanceMessage = currentMessages.some(
        msg => msg.message === CHATBOT_WHATSAPP_MESSAGE
      );

      if (!hasMaintenanceMessage) {
        if (verboseChatbot) {
          console.log(`⚠️ ${mode} is offline, showing maintenance message`);
        }
        addMessage(mode, {
          id: -Date.now(),
          from: 'bot',
          message: CHATBOT_WHATSAPP_MESSAGE,
          audio: null,
          score: 0,
          createdAt: new Date().toISOString(),
        });
      }
    }
  }, [isOpen, isCheckingBotStatus, chatbotStatusIsOnline, recommenderStatusIsOnline, skipMaintenanceMode, currentMode, storeMessages, addMessage, navigationState]);

  // Check authentication on mount
  useEffect(() => {
    // HARDCODE FOR DEVELOPMENT: Simulate authenticated user for testing
    // SECURITY: Only enabled if explicitly set to 'true' AND in development mode
    // Set VITE_HARDCODE_AUTH=true in .env to enable this (only for dev)
    const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
    // SECURITY FIX: Require explicit 'true' value, default to false for safety
    const hardcodeAuth = import.meta.env.VITE_HARDCODE_AUTH === 'true';

    if (isDevelopment && hardcodeAuth) {
      console.log('🔧 [DEV MODE] Hardcoding authentication for testing - WhatsApp flow enabled');
      console.log('🔧 [DEV MODE] Set VITE_HARDCODE_AUTH=false to disable this');

      setIsAuthenticated(true);

      // Set a test token if none exists (this will be used in WhatsApp redirect)
      const currentToken = useChatStore.getState().token;
      if (!currentToken) {
        const testToken = 'dev-test-token-' + Date.now();
        useChatStore.getState().setToken(testToken);
        console.log('🔧 [DEV MODE] Generated test token:', testToken);
      }

      // Also set hasPhoneNumber to true for testing WhatsApp redirect
      useChatStore.getState().setHasPhoneNumber(true);
      return;
    }

    // Normal authentication check
    const tokenFromCookie = getTokenUserFromCookieChatbot();
    setIsAuthenticated(!!tokenFromCookie);
  }, [setIsAuthenticated]);

  // Ref to store the latest handleOpenChatbot function
  const handleOpenChatbotRef = useRef<((options?: {
    clearUnread?: boolean;
    closeDropdown?: boolean;
    userData?: UserData | null;
    mode?: ChatMode;
  }) => void) | null>(null);

  // Ref to store the latest handleModeChange function (defined later, so ref avoids ordering issues)
  const handleModeChangeRef = useRef<((mode: ChatMode) => void) | null>(null);

  // Shared handler for opening chatbot with all tracking logic
  const handleOpenChatbot = useCallback((options?: {
    clearUnread?: boolean;
    closeDropdown?: boolean;
    userData?: UserData | null;
    mode?: ChatMode;
  }) => {
    // 1. UI State updates
    if (options?.clearUnread) setUnreadMessageCount(0);
    if (options?.closeDropdown) setIsDropdownExpanded(false);
    if (options?.userData !== undefined) setUserData(options.userData);
    
    setIsFirstRender(false);
    setIsOpen(true);
    
    // 2. Check if first open BEFORE incrementing
    const isFirstOpen = openCount === 0;
    // Check if metric was already tracked in handleToggle (openCount === 1 means it was tracked there)
    const metricAlreadyTracked = openCount === 1;
    incrementOpenCount();
    setDetectedOs(detectOSChatbot());

    // 3. Socket reconnection logic
    if (socket && openCount > 1 && socket.disconnected) {
      socket.connect();
    }

    // 4. Google Analytics tracking - only if metric wasn't already tracked in handleToggle
    // If openCount was 1, it means metric was already tracked when dropdown was expanded
    if (!metricAlreadyTracked) {
      try {
        const userId = userData?.id || getTokenUserFromCookieChatbot();
        if ((window as any).dataLayer) {
          const courseName = UrlUtils.getCourseNameFromUrl();
          (window as any).dataLayer.push({
            event: 'chatbot_open',
            chatbot_user_id: userId || 'anonymous',
            chatbot_id: uuid,
            chat_count: openCount + 1,
            item_name: courseName,
          });
        }
      } catch (error) {
        console.error('Error tracking chatbot open event:', error);
      }
    }

    // 5. Mobile scroll handling
    const detectedOs = detectOSChatbot();
    if (detectedOs === 'android' || detectedOs === 'ios') {
      document.documentElement.classList.add('bot-utn-no-scroll');
      document.documentElement.classList.remove('bot-utn-scroll');
      document.body.classList.add('bot-utn-no-scroll');
      document.body.classList.remove('bot-utn-scroll');
    }

    // 6. API call for metrics - only if not already tracked in handleToggle
    // If openCount was 1, it means metric was already tracked when dropdown was expanded
    // Only track if it's the first open (and wasn't tracked) or if it was minimized
    if ((isFirstOpen && !metricAlreadyTracked) || wasMinimized) {
      if (socket?.io?.opts?.query) {
        openCallChatbot((socket.io.opts.query as any).uuid);
      } else {
        openCallChatbot(uuid);
      }
      if (wasMinimized) {
        setWasMinimized(false);
      }
    }

    // 7. Switch to requested mode (metrics, LinkedIn state, etc. handled by handleModeChange)
    if (options?.mode === 'recommender') {
      handleModeChangeRef.current?.('recommender');
    }
  }, [
    uuid, openCount, wasMinimized, socket, userData,
    setUnreadMessageCount, setIsDropdownExpanded, setUserData,
    setIsFirstRender, setIsOpen, incrementOpenCount,
    setDetectedOs, setWasMinimized
  ]);

  // Update ref whenever handleOpenChatbot changes
  useEffect(() => {
    handleOpenChatbotRef.current = handleOpenChatbot;
  }, [handleOpenChatbot]);

  // Expose openChatbot globally and handle legacy event
  useEffect(() => {
    // 1. Expose global function (Direct call)
    (window as any).openChatbot = (data: any, mode?: string) => {
      if (verboseChatbot) {
        console.log('🔓 openChatbot called with:', data, mode);
      }
      // Use ref to always call the latest version of handleOpenChatbot
      handleOpenChatbotRef.current?.({
        userData: data || null,
        mode: mode === 'recommender' ? 'recommender' : 'chatbot',
      });
    };

    // 2. Handle legacy custom event (Event dispatch)
    const handleChatbotOpen = (event: any) => {
      if (verboseChatbot) {
        console.log('📨 chatbot-open event received:', event.detail);
      }

      if (event.detail && event.detail.open) {
        // Use ref to always call the latest version of handleOpenChatbot
        handleOpenChatbotRef.current?.({
          userData: event.detail.userData || null,
          mode: event.detail.mode === 'recommender' ? 'recommender' : 'chatbot',
        });
      }
    };

    const embeddedComponent = document.getElementById('embedded-component');
    if (embeddedComponent) {
      embeddedComponent.addEventListener('chatbot-open', handleChatbotOpen);
    }

    return () => {
      // Don't delete window.openChatbot - it should persist for the lifetime of the widget
      // Just remove the event listener
      if (embeddedComponent) {
        embeddedComponent.removeEventListener('chatbot-open', handleChatbotOpen);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  // Handler for the main dropdown button click - expands dropdown when closed
  const handleToggle = useCallback(() => {
    // If chat is not open, expand the dropdown to show options (WhatsApp + Chat)
    if (!isOpen) {
      // Track "Apertura de Chat" metric when dropdown is expanded for the first time
      const isFirstExpansion = openCount === 0 && !isDropdownExpanded;
      if (isFirstExpansion) {
        // Increment open count to track that we've registered the metric
        incrementOpenCount();
        
        // Google Analytics tracking
        try {
          const userId = userData?.id || getTokenUserFromCookieChatbot();
          if ((window as any).dataLayer) {
            const courseName = UrlUtils.getCourseNameFromUrl();
            (window as any).dataLayer.push({
              event: 'chatbot_open',
              chatbot_user_id: userId || 'anonymous',
              chatbot_id: uuid,
              chat_count: 1,
              item_name: courseName,
            });
          }
        } catch (error) {
          console.error('Error tracking chatbot open event:', error);
        }

        // API call for metrics (CRITICAL for tracking)
        if (socket?.io?.opts?.query) {
          openCallChatbot((socket.io.opts.query as any).uuid);
        } else {
          openCallChatbot(uuid);
        }
      }

      setIsDropdownExpanded(!isDropdownExpanded);

      if (verboseChatbot) {
        console.log('🔄 Toggling dropdown expanded', {
          isDropdownExpanded: !isDropdownExpanded
        });
      }
      return;
    }

    // If chat is already open, close it (EXACT same logic as old version)
    setIsFirstRender(false);
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    const newOpenCount = openCount + 1;
    incrementOpenCount();
    setDetectedOs(detectOSChatbot());

    // Apply scroll classes when opening on mobile (EXACT same as old version)
    if (newOpenState && isMobileDevice()) {
      document.documentElement.classList.add('bot-utn-no-scroll');
      document.documentElement.classList.remove('bot-utn-scroll');
      document.body.classList.add('bot-utn-no-scroll');
      document.body.classList.remove('bot-utn-scroll');
    }

    // Call openCallChatbot when opening for the first time OR after being minimized
    if (newOpenState && socket?.io?.opts?.query) {
      if (newOpenCount === 1 || wasMinimized) {
        openCallChatbot((socket.io.opts.query as any).uuid);
        if (wasMinimized) {
          setWasMinimized(false); // Reset flag after making the call
        }
      }
    }
  }, [
    isOpen,
    isDropdownExpanded,
    openCount,
    wasMinimized,
    socket,
    uuid,
    userData,
    setIsOpen,
    setIsFirstRender,
    setIsDropdownExpanded,
    incrementOpenCount,
    setDetectedOs,
    setWasMinimized,
  ]);

  // Handler for the chat button click in the expanded dropdown
  const handleChatClick = useCallback(() => {
    handleOpenChatbot({
      clearUnread: true,
      closeDropdown: true,
    });
  }, [handleOpenChatbot]);

  // Handler for the WhatsApp button click (EXACT same logic as old version)
  const handleWhatsAppClick = useCallback((tokenParam: string | null = null) => {
    setIsDropdownExpanded(false);

    if (!isAuthenticated) {
      if (verboseChatbot) {
        console.log('User not authenticated - external authentication required');
      }
      return;
    }

    // TODO: uncomment in future with feature endpoint validation information is ready
    // This matches the commented code in the old version
    // if (!hasPhoneNumber) {
    //   setShowProfileCompletionModal(true);
    //   return;
    // }

    const whatsappApiBaseUrl = import.meta.env.VITE_WHATSAPP_API_BASE_URL;
    const whatsappApiPhoneNumber = import.meta.env.VITE_WHATSAPP_API_PHONE_NUMBER;
    const whatsappApiFirstMessage = import.meta.env.VITE_WHATSAPP_API_FIRST_MESSAGE;

    if (whatsappApiBaseUrl && whatsappApiFirstMessage) {
      // Track WhatsApp click event for authenticated users
      try {
        const userId = userData?.id || getTokenUserFromCookieChatbot();
        if ((window as any).dataLayer) {
          const courseName = UrlUtils.getCourseNameFromUrl();
          (window as any).dataLayer.push({
            event: 'chatbot_open_whatsapp',
            chatbot_user_id: userId || 'anonymous',
            chatbot_id: uuid,
            item_name: courseName,
          });
        }
      } catch (error) {
        console.error('Error tracking WhatsApp open event:', error);
      }

      const params = new URLSearchParams();
      params.append('text', whatsappApiFirstMessage);
      if (tokenParam) params.append('token', tokenParam);

      window.location.href = `${whatsappApiBaseUrl}/${whatsappApiPhoneNumber}?${params.toString()}`;
    } else {
      console.error('WhatsApp API environment variables are not set');
    }
  }, [isAuthenticated, setIsDropdownExpanded, userData, uuid]);

  // Handler to close the expanded dropdown
  const handleCloseExpanded = useCallback(() => {
    setIsDropdownExpanded(false);
  }, [setIsDropdownExpanded]);

  // Close chat
  const handleClose = useCallback(() => {
    if (verboseChatbot) {
      console.log('❌ handleClose called');
    }
    setIsOpen(false);
    setWasMinimized(true); // Mark that chat was minimized
  }, [setIsOpen, setWasMinimized]);

  // Handle mode change
  const handleModeChange = useCallback((mode: ChatMode) => {
    const newNavigationState = mode === 'chatbot' ? 0 : 1;

    if (verboseChatbot) {
      console.log('🔄 Mode changed', { mode, navigationState: newNavigationState });
    }

    setNavigationState(newNavigationState);

    // Reset welcome navigation state when entering recommender ONLY if there are no messages
    if (mode === 'recommender') {
      // Call metrics endpoint for recommender open
      if (socket?.io?.opts?.query) {
        openCallRecommender((socket.io.opts.query as any).uuid);
      } else {
        openCallRecommender(uuid);
      }

      const recommenderMessages = storeMessages[CHATBOT_MODE_RECOMMENDER] || [];
      const hasRecommenderMessages = recommenderMessages.length > 0;

      if (verboseChatbot) {
        console.log('🔄 Recommender mode check', {
          hasMessages: hasRecommenderMessages,
          messageCount: recommenderMessages.length
        });
      }

      // Only reset to LinkedIn input if there are no messages
      if (!hasRecommenderMessages) {
        setWelcomeNavigationState(0);
      } else {
        // Keep in chat mode if there are already messages
        setWelcomeNavigationState(1);
      }
    }
  }, [setNavigationState, setWelcomeNavigationState, storeMessages, uuid, socket]);

  // Update ref whenever handleModeChange changes
  useEffect(() => {
    handleModeChangeRef.current = handleModeChange;
  }, [handleModeChange]);

  // Handle LinkedIn skip (go directly to chat)
  const handleLinkedInSkip = useCallback(() => {
    if (verboseChatbot) {
      console.log('⏭️ LinkedIn skipped, moving to chat');
    }

    // Just change state, the useEffect will add the initial message
    setWelcomeNavigationState(1);
  }, [setWelcomeNavigationState]);

  // Handle LinkedIn URL submit
  const handleLinkedInSubmit = useCallback((linkedinUrl: string) => {
    if (verboseChatbot) {
      console.log('🔗 LinkedIn URL submitted', { linkedinUrl });
    }

    // Move to chat state
    setWelcomeNavigationState(1);

    // Send enhanced message with LinkedIn URL
    const enhancedMessage = `Por favor recomendame cursos para mi perfil de Linkedin: ${linkedinUrl}`;

    socketSendMessage({
      from: 'client',
      message: enhancedMessage,
      typeMode: CHATBOT_MODE_RECOMMENDER,
    });
  }, [setWelcomeNavigationState, socketSendMessage]);

  // Reset chat
  const handleReset = useCallback(() => {
    // Reset welcome navigation state if in recommender mode
    if (currentMode() === CHATBOT_MODE_RECOMMENDER) {
      setWelcomeNavigationState(0);
    }

    resetChat({ preventDefault: () => { }, stopPropagation: () => { } } as React.MouseEvent);
  }, [resetChat, currentMode, setWelcomeNavigationState]);

  // Send message
  const handleSendMessage = useCallback((message: string) => {
    if (!message.trim()) return;

    const mode = currentMode();

    // Check if bot is offline
    const isCurrentModeOffline =
      (mode === CHATBOT_MODE_CHATBOT && !chatbotStatusIsOnline) ||
      (mode === CHATBOT_MODE_RECOMMENDER && !recommenderStatusIsOnline);

    if (isCurrentModeOffline) {
      if (verboseChatbot) {
        console.log('⚠️ Cannot send message, bot is offline');
      }
      return;
    }

    if (verboseChatbot) {
      console.log('📤 Sending message:', { message, mode });
    }

    // Add user message to store
    const tempId = -Date.now(); // Use temporary ID instead of uuidv4 to avoid 404 errors when rating
    addMessage(mode, {
      id: tempId,
      message: message,
      from: 'client',
      audio: null,
      score: 0,
      createdAt: new Date().toISOString(),
    });
    setInputValue('');

    // Send to server
    socketSendMessage({
      from: 'client',
      message: message,
      typeMode: mode,
    });
  }, [
    addMessage,
    setInputValue,
    socketSendMessage,
    currentMode,
    chatbotStatusIsOnline,
    recommenderStatusIsOnline,
  ]);

  // Send audio
  const handleSendAudio = useCallback((audio: string) => {
    const mode = currentMode();

    // Check if bot is offline
    const isCurrentModeOffline =
      (mode === CHATBOT_MODE_CHATBOT && !chatbotStatusIsOnline) ||
      (mode === CHATBOT_MODE_RECOMMENDER && !recommenderStatusIsOnline);

    if (isCurrentModeOffline) {
      if (verboseChatbot) {
        console.log('⚠️ Cannot send audio, bot is offline');
      }
      return;
    }

    if (verboseChatbot) {
      console.log('🎤 Sending audio:', { mode });
    }

    // Send to server
    socketSendMessage({
      from: 'client',
      message: '',
      audio: audio,
      isAudio: true,
      typeMode: mode,
    });
  }, [
    socketSendMessage,
    currentMode,
    chatbotStatusIsOnline,
    recommenderStatusIsOnline,
  ]);

  // Handle recording state change
  const handleRecordingChange = useCallback((recording: boolean) => {
    setIsRecording(recording);
  }, [setIsRecording]);

  // Show message in chat
  const handleShowMessage = useCallback((from: string, message: string) => {
    const mode = currentMode();
    const tempId = -Date.now(); // Use temporary ID
    addMessage(mode, {
      id: tempId,
      message: message,
      from: from,
      audio: null,
      score: 0,
      createdAt: new Date().toISOString(),
    });
  }, [addMessage, currentMode]);

  // Transform store messages to Message type
  const mode = currentMode();
  const messages: Message[] = useMemo(() =>
    (storeMessages[mode] || []).map((msg: any) => ({
      id: String(msg.id) || uuidv4(),
      content: msg.message || msg.content || '',
      audio: msg.audio || null,
      from: msg.from === 'bot' || msg.from === 'server' ? 'bot' : 'user',
      timestamp: msg.createdAt ? new Date(msg.createdAt) : undefined,
      isStreaming: msg.isStreaming || false,
    })),
    [storeMessages, mode]
  );

  // Determine if bot is online for current mode
  const isBotOnline = mode === CHATBOT_MODE_CHATBOT
    ? chatbotStatusIsOnline
    : recommenderStatusIsOnline;

  /*
  if (verboseChatbot) {
    console.log('📊 App state:', {
      isOpen,
      isConnected,
      messagesCount: messages.length,
      isLoading: isLoading[mode],
      mode,
      isBotOnline,
      isCheckingBotStatus,
    });
  }
  */

  // Convert mode to ChatMode type
  const chatMode: ChatMode = mode === CHATBOT_MODE_CHATBOT ? 'chatbot' : 'recommender';

  return (
    <StyleInjector>
      {/* Expanded dropdown options (WhatsApp + Chat) */}
      {isDropdownExpanded && !isOpen && (
        <ContactOptionsExpanded
          onClose={handleCloseExpanded}
          onChatClick={handleChatClick}
          onWhatsAppClick={handleWhatsAppClick}
          unreadCount={unreadMessageCount}
          isAuthenticated={isAuthenticated}
          token={token}
          hasPhoneNumber={hasPhoneNumber}
        />
      )}

      <ChatWidget
        isOpen={isOpen}
        isExpanded={isDropdownExpanded}
        mode={chatMode}
        welcomeNavigationState={welcomeNavigationState}
        messages={messages}
        isLoading={isLoading[mode] || isCheckingBotStatus}
        isConnected={isConnected && isBotOnline}
        isRecording={isRecording}
        onToggle={handleToggle}
        onClose={handleClose}
        onReset={handleReset}
        onModeChange={handleModeChange}
        onSendMessage={handleSendMessage}
        onSendAudio={handleSendAudio}
        onRecordingChange={handleRecordingChange}
        onShowMessage={handleShowMessage}
        onLinkedInSubmit={handleLinkedInSubmit}
        onLinkedInSkip={handleLinkedInSkip}
        onHitlDecision={sendHitlDecision}
      />

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showProfileCompletionModal}
        onClose={() => setShowProfileCompletionModal(false)}
        onComplete={() => {
          setHasPhoneNumber(true);
          setShowProfileCompletionModal(false);
        }}
      />
    </StyleInjector>
  );
}

export default App;
